import { cloneDeep } from 'lodash'
import { getWidth as OLExtentGetWidth } from 'ol/extent'
import OLLayerTile from 'ol/layer/Tile'
import { get as OLProjGet } from 'ol/proj'
import OLSourceTileWMS from 'ol/source/TileWMS'
import OLTilegridTileGrid from 'ol/tilegrid/TileGrid'
import TileState from 'ol/TileState'

import type { Tile } from 'ol'
import type { Extent as OLExtent } from 'ol/extent'
import type { TileImage } from 'ol/source'
import type { Options as TileWMSOptions } from 'ol/source/TileWMS'

import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import type { ConfigLayerOptions } from 'config/ConfigLayer'
import type { ConfigLayerBaseTileOptions } from 'config/ConfigLayerBaseTileOptions'
import { MapLayerWMS } from 'config/MapLayerWMS'
import { setLayerLoadDone, setLayerLoadFailed, setLayerLoadInProgress } from 'drawer/tabs/layers/actions'
import { loadImage } from 'utils/Image'
import { getMap } from 'utils/MapUtils'

import SetAttributions from './SetAttributions'

export interface ConfigLayerTileWMSOptions extends ConfigLayerOptions {
  config: ConfigLayerTileWMSOptionsConfig
}

export interface ConfigLayerTileWMSOptionsConfig extends TileWMSOptions {
  tileSize?: number
}

export class MapLayerTileWMS extends MapLayerWMS {
  static createLayer (layerConf: ConfigLayerTileWMSOptions): OLLayerTile<TileImage> {
    this.preLayer(layerConf)

    let tileLoadingCount = 0
    let atLeastOneTileLoadedSuccessfully = false

    const tileLoadingStart = (titleid: ConfigLayerTileWMSOptions['titleid']) => {
      tileLoadingCount++

      // console.debug('tileLoadingStart', titleid, tileLoadingCount)

      window.store.dispatch(setLayerLoadInProgress(titleid))
    }

    const tileLoadingEnd = (titleid: ConfigLayerTileWMSOptions['titleid']) => {
      tileLoadingCount--

      // console.debug('tileLoadingEnd', titleid, tileLoadingCount, atLeastOneTileLoadedSuccessfully)

      // Only mark layer as finished (done/failed) when there are no more tiles to load
      if (tileLoadingCount <= 0) {
        // If at least one of the tiles loaded successfully we mark the loading as done
        if (atLeastOneTileLoadedSuccessfully) {
          window.store.dispatch(setLayerLoadDone(titleid))
        } else {
          // Else, we report that the layer loading has failed
          window.store.dispatch(setLayerLoadFailed(titleid))
        }
      }
    }

    // Get the world extent and, cast it to OLExtent to keep typescript happy (because it
    // knows that these lookups can sometimes result in `undefined` values)
    const projExtent = OLProjGet('EPSG:3857')?.getExtent() as OLExtent

    // Get the tile size from the config or use the default (256 pixels)
    const tileSize = layerConf.config.tileSize || 256

    // Build the resolutions for the tile grid
    const startResolution = OLExtentGetWidth(projExtent) / tileSize
    const resolutionsTileWMS = new Array(22)
    for (let j = 0, jj = resolutionsTileWMS.length; j < jj; ++j) {
      resolutionsTileWMS[j] = startResolution / Math.pow(2, j)
    }

    const { config, titleid } = layerConf

    const { params = {}, ...restOfLayerConfig } = cloneDeep(config)

    const tileGridConf = {
      tileGrid: new OLTilegridTileGrid({
        origin: [0, 0],
        resolutions: resolutionsTileWMS,
        tileSize: [tileSize, tileSize]
      })
    }

    // Make sure all params are upper case. This is needed for automatically
    // deriving the legend URL from the OL Source
    const paramsUC: Record<string, number | string> = {}
    for (const [key, value] of Object.entries(params)) {
      paramsUC[key.toUpperCase()] = value
    }

    const olSourceOptions = {
      params: paramsUC,
      ...tileGridConf,
      ...restOfLayerConfig
    }

    const olSourceTileWMS = new OLSourceTileWMS(olSourceOptions)

    const baseTileOptions: ConfigLayerBaseTileOptions = {
      title: layerConf.title,
      titleid: layerConf.titleid,
      group: layerConf.group,
      source: olSourceTileWMS,
      maxResolution: layerConf.maxResolution || 100000,
      maxZoom: layerConf.maxZoom,
      minResolution: layerConf.minResolution || 0.0001,
      minZoom: layerConf.minZoom,
      opacity: layerConf.opacity || 1,
      visible: PoziUtils.isLayerSelected(layerConf, true)
    }

    const olLayer = new OLLayerTile(baseTileOptions)

    const tileLoadFunction = async function (tile: Tile, url: string) {
      // Check if map is animating while we are loading
      const view = getMap().getView()
      const isAnimating = view.getAnimating()
      const zoom = view.getZoom()

      // console.debug('MapLayerTileWMS tileLoadFunction', titleid, isAnimating, zoom, tile, url)

      // Do not request tiles while we are zooming in/out
      // (we will wait and check if the tile is still needed)
      // if (isAnimating) {
      //   console.log('MapLayerTileWMS - tileLoadFunction - map is animating - pausing loading tile 250ms', isAnimating, zoom, titleid, url)
      //   await PoziUtils.sleep(250) // same duration as an OL zoom animation

      //   const newZoom = view.getZoom()
      //   if (newZoom !== zoom) {
      //     console.log('MapLayerTileWMS - tileLoadFunction - zoom changed after slight delay - not loading tile', newZoom, zoom, titleid, url)
      //     return
      //   }
      // } else {
      //   // console.debug('MapLayerTileWMS - tileLoadFunction - map not animating. All good. :-)', isAnimating, zoom, titleid)
      // }

      try {
        tileLoadingStart(titleid)

        const data = await loadImage(url)
        if (data !== undefined) {
          // @ts-expect-error
          tile.getImage().src = URL.createObjectURL(data)

          atLeastOneTileLoadedSuccessfully = true
        } else {
          tile.setState(TileState.ERROR)
        }
      } catch (ex) {
        tile.setState(TileState.ERROR)
      } finally {
        tileLoadingEnd(titleid)
      }
    }

    olSourceTileWMS.setTileLoadFunction(tileLoadFunction)

    PoziUtils.setSourceLoaded(olLayer, olSourceTileWMS, layerConf)

    Model.set('app.map.ol.source.' + layerConf.titleid, olSourceTileWMS)
    Model.set('app.map.ol.layer.' + layerConf.titleid, olLayer)

    // Hack to mark layer as loaded once 1 tile has successfully loaded. We do this because
    // OL has no event for when all tiles have loaded
    olSourceTileWMS.once('tileloadend', () => {
      // Set in old model
      Model.set('app.config.layerbytitleid[' + layerConf.titleid + '].loaded', true)

      // Set in redux
      window.store.dispatch(setLayerLoadDone(layerConf.titleid))
    })

    olSourceTileWMS.on('tileloaderror', function (event) {
      // const errorMsg = 'Layer load failed ' + layerConf.title
      // PoziUtils.gtag({
      //   category: 'data',
      //   status: 'warning',
      //   identifier: layerConf.title,
      //   action: 'ajax request',
      //   additionalInfo: errorMsg
      // })
      console.error('TileWMS tile load error for layer ' + layerConf.title, event)
    })

    // Automatically derive the legend URL from the layer configuration, if not provided yet
    this.autoGenerateLegendUrl(layerConf, olLayer)

    // Add attributions for Nearmap layers
    // TODO: do this in a better way
    const url = PoziUtils.getLayerUrl(layerConf) || ''
    if (olSourceTileWMS && url.includes('nearmap.com')) {
      const attributions = [
        '<a target="_blank" rel="noopener noreferrer" href="https://nearmap.com">© Nearmap</a>'
      ]

      SetAttributions(olLayer, attributions, true)
    }

    return olLayer
  }
}
